import type { Dispatch, SetStateAction } from 'react';
import React, { createContext, useContext, useMemo, useState } from 'react';

import type { ChatNotice } from '../../types/chat.types';

export type SparkStatus = {
  isLoading: boolean;
  notice: ChatNotice | null;
  setIsLoading: Dispatch<SetStateAction<boolean>>;
  setNotice: Dispatch<SetStateAction<ChatNotice | null>>;
};

/*
  **No default.** A default would have to be a pair of no-ops, and a screen rendered outside the
  provider would then swallow every notice it tried to raise rather than saying so.
*/
export const SparkStatusContext = createContext<SparkStatus | null>(null);

export const useSparkStatus = () => {
  const status = useContext(SparkStatusContext);

  if (!status) {
    throw new Error('useSparkStatus must be used inside SparkStatusProvider');
  }

  return status;
};

/**
 * SparkStatusProvider - I hold the two things the whole screen reports: whether an answer is on
 * its way, and what went wrong. ONLY concerned with those two - every other piece of chat state
 * belongs to the hook that owns it.
 *
 * **They live here because they were threaded everywhere.** Three hooks took both setters as
 * arguments and four components took the values as props, none of which owned either.
 */
export const SparkStatusProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [notice, setNotice] = useState<ChatNotice | null>(null);

  const status = useMemo(
    () => ({ isLoading, notice, setIsLoading, setNotice }),
    [isLoading, notice]
  );

  return (
    <SparkStatusContext.Provider value={status}>
      {children}
    </SparkStatusContext.Provider>
  );
};
